import { useState, useEffect } from 'react';
import { View, Modal, Pressable, ScrollView, Alert, Share } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SubHeading, Body, Caption, ThemedText } from '@/components/ui/Text';
import { 
  shareRecommendation, 
  unshareRecommendation,
  getTripCompanions,
  type SavedRecommendation
} from '@/services/recommendations';
import { buildTemplateShareUrl } from '@/navigation/templateDeepLink';

type ShareRecommendationModalProps = {
  visible: boolean;
  onClose: () => void;
  userId: string;
  recommendation: SavedRecommendation;
};

type CompanionOption = {
  id: string;
  name: string;
  email?: string;
};

export function ShareRecommendationModal({
  visible,
  onClose,
  userId,
  recommendation,
}: ShareRecommendationModalProps) {
  const theme = useTheme();
  const [companions, setCompanions] = useState<CompanionOption[]>([]);
  const [selectedCompanionIds, setSelectedCompanionIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (visible && userId) {
      setFetching(true);
      // Initialize with existing sharedWith
      setSelectedCompanionIds(recommendation.sharedWith || []);
      
      // Fetch available companions
      getTripCompanions(userId)
        .then((companionIds: string[]) => {
          // For now, we'll use user IDs as names
          // In a real app, you'd fetch user profiles to get actual names
          const companionOptions: CompanionOption[] = companionIds.map((id: string) => ({
            id,
            name: `User ${id.substring(0, 8)}...`, // Placeholder - would fetch actual names
            email: undefined,
          }));
          setCompanions(companionOptions);
          setFetching(false);
        })
        .catch((error: unknown) => {
          console.error('Error fetching companions:', error);
          setFetching(false);
        });
    }
  }, [visible, userId, recommendation.sharedWith]);

  const toggleCompanion = (companionId: string) => {
    setSelectedCompanionIds((prev) => 
      prev.includes(companionId)
        ? prev.filter((id) => id !== companionId)
        : [...prev, companionId]
    );
  };

  const handleSave = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      if (selectedCompanionIds.length === 0) {
        // Unshare if no companions selected
        await unshareRecommendation(userId, recommendation.id);
      } else {
        // Share with selected companions
        await shareRecommendation(userId, recommendation.id, selectedCompanionIds);
      }
      
      Alert.alert(
        'Success',
        selectedCompanionIds.length === 0
          ? 'Recommendation is now private.'
          : `Recommendation shared with ${selectedCompanionIds.length} ${selectedCompanionIds.length === 1 ? 'companion' : 'companions'}.`
      );
      onClose();
    } catch (error) {
      console.error('Error sharing recommendation:', error);
      Alert.alert('Error', 'Failed to share recommendation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExternalShare = async () => {
    const title = recommendation.title || recommendation.destination;
    const templateUrl = recommendation.templateId
      ? buildTemplateShareUrl({ templateId: recommendation.templateId, source: 'bucket_list_share' })
      : null;
    const shareText = templateUrl
      ? `Check out this Tripvio template: ${title}\n${templateUrl}`
      : `Check out this Tripvio trip idea: ${title}`;
    try {
      await Share.share({
        title,
        message: shareText,
        ...(templateUrl ? { url: templateUrl } : {}),
      });
    } catch (error) {
      if (error instanceof Error && error.message.includes('User did not share')) return;
      Alert.alert('Share failed', 'Unable to open share sheet right now.');
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
      presentationStyle="overFullScreen"
    >
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.overlay,
          justifyContent: 'flex-end',
        }}
      >
        <Pressable style={{ flex: 1 }} onPress={onClose} />
        <View
          style={{
            backgroundColor: theme.colors.surface,
            borderTopLeftRadius: theme.radii.xl,
            borderTopRightRadius: theme.radii.xl,
            height: '82%',
            paddingBottom: theme.spacing(4),
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: theme.spacing(2),
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.borderMuted,
            }}
          >
            <SubHeading>Share Recommendation</SubHeading>
            <Pressable onPress={onClose}>
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </Pressable>
          </View>

          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              padding: theme.spacing(2),
              gap: theme.spacing(2),
            }}
          >
            <Card variant="outlined">
              <SubHeading>{recommendation.destination}</SubHeading>
              <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                Select trip companions to share this recommendation with.
              </Body>
              <Button
                label="Share externally"
                variant="secondary"
                onPress={handleExternalShare}
                icon={<Ionicons name="share-social-outline" size={18} color={theme.colors.primary} />}
                style={{ marginTop: theme.spacing(1.5) }}
              />
            </Card>

            {fetching ? (
              <View style={{ padding: theme.spacing(2) }}>
                <Body color={theme.colors.textSecondary}>Loading companions...</Body>
              </View>
            ) : companions.length === 0 ? (
              <Card variant="outlined">
                <Body color={theme.colors.textSecondary}>
                  No trip companions found yet. You can still use Share externally above.
                </Body>
              </Card>
            ) : (
              <Card variant="outlined">
                <SubHeading style={{ marginBottom: theme.spacing(1) }}>Select companions</SubHeading>
                <View style={{ gap: theme.spacing(1) }}>
                  {companions.map((companion) => {
                    const isSelected = selectedCompanionIds.includes(companion.id);
                    return (
                      <Pressable
                        key={companion.id}
                        onPress={() => toggleCompanion(companion.id)}
                        style={({ pressed }) => ({
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: theme.spacing(1.5),
                          borderRadius: theme.radii.md,
                          borderWidth: 1,
                          borderColor: isSelected ? theme.colors.primary : theme.colors.border,
                          backgroundColor: isSelected ? theme.colors.primary + '15' : theme.colors.surface,
                          opacity: pressed ? 0.7 : 1,
                        })}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                          <Ionicons
                            name={isSelected ? 'checkbox' : 'checkbox-outline'}
                            size={24}
                            color={isSelected ? theme.colors.primary : theme.colors.textSecondary}
                          />
                          <View>
                            <ThemedText variant="bodyStrong">{companion.name}</ThemedText>
                            {companion.email && (
                              <Caption color={theme.colors.textSecondary}>{companion.email}</Caption>
                            )}
                          </View>
                        </View>
                      </Pressable>
                    );
                  })}
                </View>
              </Card>
            )}

            <View style={{ flexDirection: 'row', gap: theme.spacing(1.5) }}>
              <Button
                label="Cancel"
                variant="ghost"
                onPress={onClose}
                disabled={loading}
                style={{ flex: 1 }}
              />
              <Button
                label={loading ? 'Saving...' : 'Save'}
                onPress={handleSave}
                disabled={loading || fetching}
                style={{ flex: 1 }}
              />
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

